import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { logger } from '@/utils/logger';
import { useToast } from '@/hooks/use-toast';
import { trackAddToCart, trackCartAbandon } from '@/hooks/use-analytics';

export interface CartItem {
  id: string;
  name: string;
  casNumber: string;
  category: string;
  description: string;
  availability: "In Stock" | "On Demand" | "Custom";
  certifications: string[];
  quantity: number;
  addedAt: Date;
  price: {
    value: number;
    unit: string;
    currency: string;
    moq: number; // Minimum Order Quantity
  };
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: Omit<CartItem, 'quantity' | 'addedAt'> }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'TOGGLE_CART' }
  | { type: 'OPEN_CART' }
  | { type: 'CLOSE_CART' }
  | { type: 'LOAD_CART'; payload: CartItem[] };

const initialState: CartState = {
  items: [],
  isOpen: false,
};

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItem = state.items.find(item => item.id === action.payload.id);

      if (existingItem) {
        // When adding an item that's already in the cart, bump by the MOQ
        // step (or 1 if no MOQ). This matches the "add another unit" intent
        // without dropping below MOQ on repeated clicks.
        const step = Math.max(1, action.payload.price?.moq ?? 1);
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + step }
              : item
          ),
        };
      }

      // New item: start at the MOQ so the customer never sees a MOQ-violation
      // banner at checkout for the default-quantity case. They can still
      // adjust down in the cart drawer if they need a custom-quote flow.
      const initialQty = Math.max(1, action.payload.price?.moq ?? 1);
      return {
        ...state,
        items: [
          ...state.items,
          {
            ...action.payload,
            quantity: initialQty,
            addedAt: new Date(),
          },
        ],
      };
    }
    
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload),
      };
    
    case 'UPDATE_QUANTITY':
      if (action.payload.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(item => item.id !== action.payload.id),
        };
      }
      
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };
    
    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
      };
    
    case 'TOGGLE_CART':
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    
    case 'OPEN_CART':
      return {
        ...state,
        isOpen: true,
      };
    
    case 'CLOSE_CART':
      return {
        ...state,
        isOpen: false,
      };
    
    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload,
      };
    
    default:
      return state;
  }
};

interface CartContextType {
  state: CartState;
  addItem: (item: Omit<CartItem, 'quantity' | 'addedAt'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  getTotalItems: () => number;
  getTotalValue: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [isInitialized, setIsInitialized] = useState(false);
  const { toast } = useToast();

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('rfq-cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        if (!Array.isArray(parsedCart)) {
          throw new Error('Persisted cart is not an array');
        }
        /* Guard against schema drift — earlier revisions of the app
           persisted items without price.value/.moq/.currency (or with
           NaN quantities from a broken quantity picker). Returning
           buyers would then see NaN totals and MOQ banners that never
           cleared. Drop lines that fail the shape check, and coerce
           addedAt back to Date. */
        const itemsWithDates = parsedCart
          .filter((item: unknown) => {
            if (!item || typeof item !== 'object') return false;
            const i = item as Partial<CartItem>;
            return typeof i.id === 'string'
              && typeof i.name === 'string'
              && typeof i.quantity === 'number' && Number.isFinite(i.quantity) && i.quantity > 0
              && i.price != null
              && typeof (i.price as { value?: unknown }).value === 'number'
              && Number.isFinite((i.price as { value: number }).value)
              && typeof (i.price as { moq?: unknown }).moq === 'number'
              && Number.isFinite((i.price as { moq: number }).moq);
          })
          .map((item: CartItem) => ({
            ...item,
            addedAt: new Date(item.addedAt),
          }));
        dispatch({ type: 'LOAD_CART', payload: itemsWithDates });
        if (itemsWithDates.length < parsedCart.length) {
          logger.warn(
            `Cart hydration dropped ${parsedCart.length - itemsWithDates.length} corrupt line(s)`,
          );
        }
      }
    } catch (error) {
      logger.error('Error loading cart from localStorage:', error);
      // Persisted cart is corrupted (likely from a previous version). Reset
      // so the next save doesn't fail again, and warn the user.
      localStorage.removeItem('rfq-cart');
      toast({
        title: 'Cart reset',
        description: 'Saved cart data was corrupted and has been cleared.',
        variant: 'destructive',
      });
    } finally {
      setIsInitialized(true);
    }
  }, [toast]);

  // Save cart to localStorage whenever it changes (only after initialization)
  useEffect(() => {
    if (!isInitialized) return;
    
    try {
      localStorage.setItem('rfq-cart', JSON.stringify(state.items));
    } catch (error) {
      logger.error('Error saving cart to localStorage:', error);
    }
  }, [state.items, isInitialized]);

  // Fire `cart_abandon` when the user closes the tab / navigates off with a
  // non-empty cart that hasn't been checked out. `pagehide` fires more
  // reliably than `beforeunload` on mobile Safari + iOS, and the analytics
  // helper uses sendBeacon so the event survives the unload race. We
  // intentionally don't track every cart deletion — only "left with stuff".
  useEffect(() => {
    if (!isInitialized) return;
    const handler = () => {
      if (state.items.length === 0) return;
      const value = state.items.reduce(
        (sum, it) => sum + (it.price?.value ?? 0) * it.quantity,
        0,
      );
      trackCartAbandon(state.items.length, value || null);
    };
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
  }, [state.items, isInitialized]);

  const addItem = (item: Omit<CartItem, 'quantity' | 'addedAt'>) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
    // Typed helper — ensures props.cas_number / .quantity / .unit_price land
    // in the analytics_events JSONB with the exact shape the dashboard
    // aggregates on. Pre-rewrite the event payload used `casNumber` and the
    // dashboard had to do a defensive `props.casNumber ?? props.cas_number`
    // dance every render.
    const moqQty = Math.max(1, item.price?.moq ?? 1);
    trackAddToCart(item.casNumber, item.name, item.category, moqQty, item.price?.value);
  };

  const removeItem = (id: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  };

  const updateQuantity = (id: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const toggleCart = () => {
    dispatch({ type: 'TOGGLE_CART' });
  };

  const openCart = () => {
    dispatch({ type: 'OPEN_CART' });
  };

  const closeCart = () => {
    dispatch({ type: 'CLOSE_CART' });
  };

  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalValue = () => {
    return state.items.reduce((total, item) => {
      const itemTotal = item.price.value * item.quantity;
      return total + itemTotal;
    }, 0);
  };

  const value: CartContextType = {
    state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    toggleCart,
    openCart,
    closeCart,
    getTotalItems,
    getTotalValue,
  };

  // Don't render children until cart is initialized
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};